import {
  Container,
  Stack,
  Typography,
  Breadcrumbs,
  Box,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import { Link } from "react-router-dom";
import { useIntl } from "react-intl";
import { useState } from "react";
import ODataGrid, { GridCol } from "../components/oDataGrid";
import { getBaseUrl } from "../core/config";
import { DropdownOdata } from "../components/DropdownOdata";
import { AppClient } from "../services/client.service";
import ConfirmDialog from "../components/common/ConfirmDialog";

export default function TesterPage() {
  const intl = useIntl();
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/users/app`;
  const [refreshKey, setRefreshKey] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [dropdownError, setDropdownError] = useState<string | null>(null);
  const [addingTester, setAddingTester] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<string | null>(null);
  const [removingTester, setRemovingTester] = useState(false);

  const columns: GridCol[] = [
    {
      field: "id",
      headerName: "id",
      type: "string",
      width: 200,
    },
    {
      field: "name",
      headerName: "name",
      type: "string",
      width: 150,
    },
    {
      field: "lastName",
      headerName: "lastName",
      type: "string",
      width: 150,
    },
    {
      field: "email",
      headerName: "email",
      type: "string",
      width: 200,
    },
    {
      field: "isActive",
      headerName: "isActive",
      type: "boolean",
      width: 120,
    },
    {
      field: "phoneNumber",
      headerName: "phoneNumber",
      type: "string",
      width: 150,
    },
    {
      field: "postalCode",
      headerName: "postalCode",
      type: "string",
      width: 120,
    },
    {
      field: "country",
      headerName: "country",
      type: "string",
      width: 120,
    },
    {
      field: "city",
      headerName: "city",
      type: "string",
      width: 150,
    },
    {
      field: "address",
      headerName: "address",
      type: "string",
      width: 200,
    },
    {
      field: "updateDate",
      headerName: "updateDate",
      type: "dateTime",
      width: 180,
    },
    {
      field: "__actions__",
      headerName: "",
      type: "string",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        const userId = params.row?.id;
        return (
          <Tooltip title={intl.formatMessage({ id: "removeTester" })}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (userId) {
                  setUserToRemove(userId);
                  setConfirmDialogOpen(true);
                }
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  // Pre-filter to show only testers
  const preFilter = "isTester eq true";

  const handleReload = () => {
    setRefreshKey(refreshKey + 2);
  };

  const handleAddTester = () => {
    setDialogOpen(true);
    setSelectedUserId(null);
    setDropdownError(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUserId(null);
    setDropdownError(null);
  };

  const handleConfirmAdd = async () => {
    if (!selectedUserId) {
      setDropdownError("No user selected");
      return;
    }
    setAddingTester(true);
    try {
      await AppClient.getInstance().tester(selectedUserId, AppClient.version, {
        isTester: true,
      } as any);
      handleCloseDialog();
      handleReload();
    } catch (error) {
      console.error("Error adding tester:", error);
      setDropdownError(intl.formatMessage({ id: "error" }));
    } finally {
      setAddingTester(false);
    }
  };

  const handleRemoveTester = async () => {
    if (!userToRemove) return;
    setRemovingTester(true);
    try {
      await AppClient.getInstance().tester(userToRemove, AppClient.version, {
        isTester: false,
      } as any);
      setConfirmDialogOpen(false);
      setUserToRemove(null);
      handleReload();
    } catch (error) {
      console.error("Error removing tester:", error);
    } finally {
      setRemovingTester(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Stack spacing={2} sx={{ p: 2 }}>
        <Box>
          <Breadcrumbs aria-label="breadcrumb">
            <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
              {intl.formatMessage({ id: "dashboard" })}
            </Link>
            <Link
              to="/app-users"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              {intl.formatMessage({ id: "appUsers" })}
            </Link>
            <Typography color="text.primary">
              {intl.formatMessage({ id: "testers" })}
            </Typography>
          </Breadcrumbs>

          <Box>
            <Typography variant="h4">
              {intl.formatMessage({ id: "testers" })}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <IconButton
              aria-label={intl.formatMessage({ id: "reloadButtonLabel" })}
              onClick={handleReload}
            >
              <RefreshIcon />
            </IconButton>
            <Button variant="contained" onClick={handleAddTester}>
              {intl.formatMessage({ id: "addTester" })}
            </Button>
          </Box>
          <Box sx={{ height: "calc(100vh - 226px)", minHeight: 400 }}>
            <ODataGrid
              key={"gridTester" + refreshKey}
              url={url}
              columns={columns}
              pageSize={20}
              preFilter={preFilter}
              refreshKey={refreshKey}
            />
          </Box>
        </Box>
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{intl.formatMessage({ id: "addTester" })}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <DropdownOdata
              url="users/app"
              fieldNameId="id"
              fieldNameDescription="email"
              value={selectedUserId}
              onChange={(newValue) => {
                setSelectedUserId(newValue);
                setDropdownError(null);
              }}
              error={dropdownError}
              label="email"
              top={12}
              searchField="email"
              debounceTime={400}
              preFilter="isTester eq false"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Box sx={{ pr: 2, pb: 2 }}>
            <Button onClick={handleCloseDialog}>
              {intl.formatMessage({ id: "deleteCancelLabel" })}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmAdd}
              sx={{ ml: 2 }}
              disabled={addingTester}
              startIcon={addingTester ? <CircularProgress size={16} /> : null}
            >
              {intl.formatMessage({ id: "addTester" })}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => {
          if (!removingTester) {
            setConfirmDialogOpen(false);
            setUserToRemove(null);
          }
        }}
        onConfirm={handleRemoveTester}
        title={intl.formatMessage({ id: "removeTesterConfirmTitle" })}
        message={intl.formatMessage({ id: "removeTesterConfirmMessage" })}
        confirmLabel={intl.formatMessage({ id: "removeTesterConfirmLabel" })}
        loading={removingTester}
      />
    </Container>
  );
}
